import { useQuery } from '@apollo/client/react'
import { SearchSelect } from '@src/components/Elements/SearchSelect'
import React, { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash.debounce'
import { StringPaginationType } from '@src/generated/schema'
import { SearchSelectProps } from '@src/components/Elements/SearchSelect/SearchSelect'
import { Account } from '@src/graphql/Account'

export const FirstNameSelect: FC<Pick<SearchSelectProps, 'required'>> = (props) => {
  const { t } = useTranslation()
  const { data, loading, fetchMore, refetch } = useQuery<{ firstNames: StringPaginationType }>(Account.firstNames, {
    variables: {
      pageSize: 10,
      page: 1,
    },
  })
  const { firstNames: { page, strings, hasNext } = { page: 0, strings: [], hasNext: false } } = data || {}

  const handleSearch = useCallback(
    debounce((filterValue: string) => {
      refetch({ filterValue, pageSize: 10, page: 1 })
    }, 100),
    [],
  )

  const handlePopupScroll = (e: any) => {
    e.persist()
    const scrollTop = e.target.scrollTop as number
    const offsetHeight = e.target.offsetHeight as number

    if (scrollTop + offsetHeight === e.target.scrollHeight && hasNext) {
      const previousPage: number = page || 0

      fetchMore({
        variables: {
          page: previousPage + 1,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const newPage = fetchMoreResult?.firstNames?.page || 0
          const oldPage = prev?.firstNames?.page || 0

          if (!fetchMoreResult || newPage <= oldPage) return prev

          const newEdges = fetchMoreResult.firstNames.strings
          const previousEdges = prev?.firstNames?.strings || []

          return newEdges?.length
            ? {
                firstNames: {
                  page: fetchMoreResult.firstNames.page,
                  pages: fetchMoreResult.firstNames.pages,
                  hasNext: fetchMoreResult.firstNames.hasNext,
                  hasPrevious: fetchMoreResult.firstNames.hasPrevious,
                  __typename: prev.firstNames.__typename,
                  strings: [...previousEdges, ...newEdges],
                },
              }
            : prev
        },
      })
    }
  }

  return (
    <SearchSelect
      {...props}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      label={t('general.firstName')}
      options={strings?.map((firstName) => ({
        title: firstName,
        value: firstName,
      }))}
      name='firstName'
      loading={loading}
    />
  )
}
